import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Modal } from "react-bootstrap";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import { stripMarkdownFromTitle } from "../../helper/stripMarkdownTitle";
import { formatScheduleDateTime } from "../../helper/dateTimeFormatter";
import {
  articleAssignedOnlyToDept,
  canManageCompanyArticle,
  getArticleDeptIds,
  isCompanyAdminMembership,
  isDeptAdminMembership,
} from "../../helper/articleAdminRights";
import CreateArticleChoose from "../Feeds/CreateArticleChoose";
import "./CompanyUnpublishedArticles.css";

function isUnpublishedArticle(article) {
  if (!article) return false;
  return (
    article.isPublished === false ||
    article.isPublished === 0 ||
    article.isPublished === "false" ||
    article.isPublished === "0"
  );
}

function isScheduledArticle(article) {
  if (!isUnpublishedArticle(article)) return false;
  const publishAt = Number(article.publishedAt);
  return Number.isFinite(publishAt) && publishAt * 1000 > Date.now();
}

function normalizeUnpublishedArticle(article) {
  return {
    id: article.id,
    title: article.title || "",
    isPublished: article.isPublished,
    publishedAt: article.publishedAt || null,
    articleType: !!article.articleType,
    deptLocation: article.deptLocation || "",
    postedByName:
      article.postedByName ||
      (article.user
        ? `${article.user.firstName || ""} ${article.user.lastName || ""}`.trim()
        : ""),
  };
}

/**
 * Scheduled articles and drafts. Same block as the company profile,
 * shown for company admins (all departments) and department admins (their department).
 */
export default function CompanyUnpublishedArticles({ company, className = "" }) {
  const { isGerm, strings, language } = useContext(LanguageContext);
  const history = useHistory();
  const [articles, setArticles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [typeMenu, setTypeMenu] = useState(null);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const filterRef = useRef(null);
  const companyId = company && company.id;

  const canManage = isCompanyAdminMembership(company) || isDeptAdminMembership(company);

  useEffect(() => {
    if (!companyId || !canManage) {
      setArticles([]);
      return undefined;
    }
    let cancelled = false;
    const lang = language || (isGerm ? "de" : "en");
    const deptParams =
      isDeptAdminMembership(company) && company.deptLocationId
        ? { deptLocation: company.deptLocationId }
        : {};

    const attachDeptLocations = async (list) => {
      const rows = Array.isArray(list) ? list : [];
      if (!isDeptAdminMembership(company)) return rows;
      return Promise.all(
        rows.map(async (article) => {
          if (!article?.id || getArticleDeptIds(article).length > 0) return article;
          try {
            const { data } = await callAPI(`/article/${article.id}`, { noTranslation: true }, "GET");
            const detail = Array.isArray(data?.article) ? data.article[0] : data?.article;
            return { ...article, deptLocation: detail?.deptLocation || article.deptLocation || "" };
          } catch (_) {
            return article;
          }
        })
      );
    };

    const load = async () => {
      try {
        const response = await callAPI(
          `/company/${companyId}/unpublishedArticles`,
          { lang, ...deptParams },
          "GET"
        );
        if (
          !cancelled &&
          response?.status === 200 &&
          response?.data?.status &&
          Array.isArray(response.data.articles)
        ) {
          setArticles(await attachDeptLocations(response.data.articles));
          return;
        }
      } catch (_) {}
      try {
        const { data } = await callAPI(`/company/${companyId}/getCompanyArticle`, { lang, ...deptParams }, "GET");
        const rows = Array.isArray(data?.articles) ? data.articles : [];
        if (!cancelled) {
          setArticles(
            await attachDeptLocations(rows.filter(isUnpublishedArticle).map(normalizeUnpublishedArticle))
          );
        }
      } catch (_) {
        if (!cancelled) setArticles([]);
      }
    };

    setStatusFilter("all");
    setTypeFilter("all");
    setTypeMenu(null);
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, canManage, company && company.deptLocationId, company && company.role, company && company.position, language, isGerm]);

  useEffect(() => {
    if (!typeMenu) return undefined;
    const onDown = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) setTypeMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [typeMenu]);

  if (!companyId || !canManage) return null;

  const inScope = (article) => {
    if (isCompanyAdminMembership(company)) return true;
    if (!isDeptAdminMembership(company)) return false;
    return !!article.articleType && articleAssignedOnlyToDept(article, company.deptLocationId);
  };
  const matchesStatus = (article) => {
    if (statusFilter === "scheduled") return isScheduledArticle(article);
    if (statusFilter === "drafts") return !isScheduledArticle(article);
    return true;
  };
  const matchesType = (article) => {
    if (typeFilter === "internal") return !!article.articleType;
    if (typeFilter === "external") return !article.articleType;
    return true;
  };
  const scoped = articles.filter(inScope);
  const visible = scoped.filter((article) => matchesStatus(article) && matchesType(article));
  const emptyMessage =
    statusFilter === "scheduled" && typeFilter === "all"
      ? strings.no_scheduled_articles || "No scheduled articles"
      : statusFilter === "drafts" && typeFilter === "all"
        ? strings.no_draft_articles || "No drafts"
        : strings.no_scheduled_or_drafts || "No scheduled articles or drafts";

  const editState = (article) => ({
    from: article.articleType ? "internal" : "external",
    articleType: article.articleType ? 1 : 0,
    companyId,
  });

  const openCreate = () => {
    if (isCompanyAdminMembership(company)) {
      setShowCreate(true);
      return;
    }
    if (isDeptAdminMembership(company)) {
      history.push({
        pathname: `/add-company-article/${companyId}`,
        state: {
          from: "internal",
          articleType: 1,
          companyId,
          deptLocationId: company.deptLocationId,
        },
      });
    }
  };

  const confirmDelete = async () => {
    const article = articleToDelete;
    setArticleToDelete(null);
    if (!article?.id) return;
    try {
      const { data } = await callAPI(`/article/${article.id}`, {}, "DELETE");
      $crud.notify({
        type: data.status ? "success" : "error",
        message: data.message,
      });
      if (data.status) setArticles((prev) => prev.filter((item) => item.id !== article.id));
    } catch (_) {
      $crud.notify({
        type: "error",
        message: isGerm ? "Aktion fehlgeschlagen." : "Failed to delete article.",
      });
    }
  };

  return (
    <div className={`company-unpublished-section ${className}`.trim()}>
      <div className="company-unpublished-header">
        <div className="company-employee-heading">
          <span
            className="material-symbols-outlined company-unpublished__heading-icon"
          >
            schedule
          </span>
          {strings.scheduled_and_drafts || "Scheduled articles and drafts"}
        </div>
        <button
          type="button"
          className="company-add-article-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
          onClick={openCreate}
          title={strings.create_article || "Create article"}
          aria-label={strings.create_article || "Create article"}
        >
          <span className="material-symbols-outlined">add_circle</span>
        </button>
      </div>
      <div className="company-unpublished-filter" ref={filterRef}>
        {[
          { id: "all", label: strings.filter_all || "All" },
          { id: "scheduled", label: strings.article_scheduled || "Scheduled" },
          { id: "drafts", label: strings.article_drafts || strings.email_folder_drafts || "Drafts" },
        ].map((tab) => {
          const isActive = statusFilter === tab.id;
          const menuOpen = typeMenu === tab.id;
          const tabArticles = scoped.filter((article) => {
            if (tab.id === "scheduled") return isScheduledArticle(article);
            if (tab.id === "drafts") return !isScheduledArticle(article);
            return true;
          });
          const tabCount = tabArticles.filter(matchesType).length;
          const typeLabel =
            typeFilter === "internal"
              ? strings.internal_project || "Intern"
              : typeFilter === "external"
                ? strings.external_projects || "Extern"
                : "";
          return (
            <div key={tab.id} className={`company-unpublished-tab-wrap ${isActive ? "is-active" : ""} ${menuOpen ? "is-open" : ""}`}>
              <div className={`company-unpublished-tab ${isActive ? "is-active" : ""}`}>
                <button
                  type="button"
                  className="company-unpublished-tab-main"
                  onClick={() => {
                    setStatusFilter(tab.id);
                    setTypeMenu(null);
                  }}
                >
                  <span>
                    {tab.label} ({tabCount})
                  </span>
                  {isActive && typeLabel ? <span className="company-unpublished-tab-sublabel">{typeLabel}</span> : null}
                </button>
                <button
                  type="button"
                  className={`company-unpublished-tab-caret ${menuOpen ? "is-open" : ""}`}
                  aria-expanded={menuOpen}
                  onClick={(event) => {
                    event.stopPropagation();
                    setStatusFilter(tab.id);
                    setTypeMenu(menuOpen ? null : tab.id);
                  }}
                >
                  <span className="material-symbols-outlined">expand_more</span>
                </button>
              </div>
              {menuOpen ? (
                <div className="company-unpublished-tab-menu" role="menu">
                  {[
                    { id: "all", label: strings.filter_all || "All" },
                    { id: "internal", label: strings.internal_project || "Intern" },
                    { id: "external", label: strings.external_projects || "Extern" },
                  ].map((option) => {
                    const optionCount = tabArticles.filter((article) => {
                      if (option.id === "internal") return !!article.articleType;
                      if (option.id === "external") return !article.articleType;
                      return true;
                    }).length;
                    return (
                      <button
                        type="button"
                        key={option.id}
                        role="menuitem"
                        className={`company-unpublished-tab-option ${typeFilter === option.id ? "is-selected" : ""}`}
                        onClick={() => {
                          setStatusFilter(tab.id);
                          setTypeFilter(option.id);
                          setTypeMenu(null);
                        }}
                      >
                        <span>{option.label}</span>
                        <span>{optionCount}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {visible.length === 0 ? (
        <p className="company-unpublished-empty">{emptyMessage}</p>
      ) : (
        visible.map((article) => {
          const scheduled = isScheduledArticle(article);
          const manageable = canManageCompanyArticle(article, company, { isInternal: !!article.articleType });
          return (
            <div key={article.id} className={`company-unpublished-row ${scheduled ? "is-scheduled" : "is-draft"}`}>
              <div className={`company-unpublished-kind ${scheduled ? "scheduled" : "draft"}`}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  {scheduled ? "schedule" : "edit_note"}
                </span>
              </div>
              <div className="company-unpublished-row-main">
                {manageable ? (
                  <Link
                    to={{ pathname: `/edit-company-article/${companyId}/${article.id}`, state: editState(article) }}
                    className="company-unpublished-title"
                  >
                    {stripMarkdownFromTitle(article.title || "")}
                  </Link>
                ) : (
                  <span className="company-unpublished-title">{stripMarkdownFromTitle(article.title || "")}</span>
                )}
                <div className="company-unpublished-meta">
                  <span className={`company-unpublished-type ${article.articleType ? "internal" : "external"}`}>
                    {article.articleType ? strings.internal_project || "Intern" : strings.external_projects || "Extern"}
                  </span>
                  <span className={`company-unpublished-status ${scheduled ? "scheduled" : "draft"}`}>
                    {scheduled ? strings.article_scheduled || "Scheduled" : strings.article_draft || "Draft"}
                  </span>
                  {scheduled && article.publishedAt ? (
                    <span className="company-unpublished-when">
                      {formatScheduleDateTime(
                        new Date(Number(article.publishedAt) * 1000),
                        null,
                        language || (isGerm ? "de" : "en"),
                        strings.at_time
                      )}
                    </span>
                  ) : null}
                </div>
              </div>
              {manageable ? (
                <div className="company-unpublished-actions">
                  <Link
                    to={{ pathname: `/edit-company-article/${companyId}/${article.id}`, state: editState(article) }}
                    className="kj-btn kj-btn--icon kj-btn--neutral"
                    title={strings.edit}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </Link>
                  <button type="button" className="kj-btn kj-btn--icon kj-btn--neutral" title={strings.delete_label} onClick={() => setArticleToDelete(article)}>
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              ) : null}
            </div>
          );
        })
      )}
      {showCreate ? (
        <CreateArticleChoose
          close={() => setShowCreate(false)}
          companyId={companyId}
          deptLocationId={company.deptLocationId}
        />
      ) : null}
      <Modal show={!!articleToDelete} onHide={() => setArticleToDelete(null)} animation={false} centered dialogClassName="modal-dialog-centered">
        <Modal.Body className="modal-body-relative">
          <p>{strings.delete_article_confirm}</p>
        </Modal.Body>
        <Modal.Footer className={company.isKjuup ? "internal-article-footer company-unpublished__delete-footer" : "company-unpublished__delete-footer"}>
          <button type="button" className={`kj-btn kj-btn--secondary${company.isKjuup ? " kj-btn--internal" : ""}`} onClick={() => setArticleToDelete(null)}>
            {strings.cancel}
          </button>
          <button type="button" className={`kj-btn kj-btn--outline${company.isKjuup ? " kj-btn--internal" : ""}`} onClick={confirmDelete}>
            {strings.ok}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
